import { FolderService } from "../../../service/FolderService";
import { Folder } from "../../../util/Types/Folder";
import { Space } from "../../../util/Types/Sidebar";
import { isVirtualFolderId } from "./utils";

export const VECTOR_MEMORY_FOLDERS_MIGRATION_STORAGE_KEY =
  "layoutBeta_vectorMemoryFoldersMigrated_v1";

function isVectorMemoryFolderName(name: string | undefined): boolean {
  return name?.trim().toLowerCase() === "vector memory";
}

function collectVectorMemoryFoldersWithDepth(
  tree: Folder[],
  depth: number,
): { id: string; depth: number }[] {
  const out: { id: string; depth: number }[] = [];
  for (const folder of tree) {
    if (
      isVectorMemoryFolderName(folder.name) &&
      !isVirtualFolderId(folder.id)
    ) {
      out.push({ id: folder.id, depth });
    }
    if (folder.folders?.length) {
      out.push(
        ...collectVectorMemoryFoldersWithDepth(folder.folders, depth + 1),
      );
    }
  }
  return out;
}

export async function migrateVectorMemoryFoldersOnce(
  folders: Folder[],
  spaces: Space[],
  refresh: () => void,
): Promise<void> {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(VECTOR_MEMORY_FOLDERS_MIGRATION_STORAGE_KEY)) return;

  const fromPrivate = collectVectorMemoryFoldersWithDepth(folders, 0);
  const fromSpaces = spaces.flatMap((s) =>
    collectVectorMemoryFoldersWithDepth(s.folder || [], 0),
  );
  const byId = new Map<string, number>();
  for (const { id, depth } of [...fromPrivate, ...fromSpaces]) {
    const prev = byId.get(id);
    if (prev === undefined || depth > prev) byId.set(id, depth);
  }
  const sorted = [...byId.entries()]
    .map(([id, depth]) => ({ id, depth }))
    .sort((a, b) => b.depth - a.depth);

  for (const { id } of sorted) {
    try {
      const res = await FolderService.getOne(id);
      const data = res.data;
      const flowsLen = data.flows?.length ?? 0;
      const foldersLen = data.folders?.length ?? 0;
      if (flowsLen > 0 || foldersLen > 0) continue;
      await FolderService.deleteFolder(id);
      refresh();
    } catch {
      continue;
    }
  }

  localStorage.setItem(VECTOR_MEMORY_FOLDERS_MIGRATION_STORAGE_KEY, "1");
}
